import {
  Component, ElementRef, HostListener, inject, Inject, PLATFORM_ID, Renderer2, ViewChild,
} from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthStateService } from '../../shared/states/auth.state';
import { filter, finalize, Observable } from 'rxjs';
import { Notification } from '../../components/header/notification/notification';
import { AuthService } from '../../shared/services/auth.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ChatService } from '../../shared/services/chat.service';
import { UserDashboardDropdown } from '../../components/header/user-dashboard-dropdown/user-dashboard-dropdown';
import { ShareLoveMenu } from '../../components/header/share-love-menu/share-love-menu';
import { CreateContentModals } from '../../components/create-content/create-content-modals';

@Component({
  selector: 'app-app-dashboard-header',
  imports: [
    CommonModule,
    RouterModule,
    UserDashboardDropdown,
    Notification,
    ShareLoveMenu,
    CreateContentModals,
  ],
  templateUrl: './app-dashboard-header.html',
})
export class AppDashboardHeader {
  isApplicationMenuOpen = false;
  readonly isMobileOpen$;
  authState$!: Observable<any>;
  currentUrl = '';
  isNotificationPanelOpen = false;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(UserDashboardDropdown) userDropdown?: UserDashboardDropdown;
  @ViewChild(ShareLoveMenu) shareMenu?: ShareLoveMenu;
  /** Shared Create post / Create event dialogs (also used by AppHeader). */
  @ViewChild(CreateContentModals) createContent?: CreateContentModals;

  private removeKeydownListener!: () => void;
  authLoaded = false;
  isLoggingOut = false;

  get unreadNotifCount$() { return this.notificationService.unreadCount$; }
  private chatService = inject(ChatService);
  get totalUnreadChat$() { return this.chatService.totalUnread$; }

  constructor(
    public sidebarService: SidebarService,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authState: AuthStateService,
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
  ) {
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.authState$ = this.authState.auth$;
    this.authState$.subscribe(() => {
      this.authLoaded = true;
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentUrl = event.url;
        this.closeApplicationMenu();
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      this.isApplicationMenuOpen &&
      !target.closest('[data-app-menu-panel]') &&
      !target.closest('[data-app-menu-toggle]')
    ) {
      this.closeApplicationMenu();
    }
  }

  handleToggle() {
    if (isPlatformBrowser(this.platformId)) {
      if (window.innerWidth >= 1280) {
        this.sidebarService.toggleExpanded();
      } else {
        this.sidebarService.toggleMobileOpen();
      }
    }
  }

  toggleApplicationMenu() {
    this.setApplicationMenuState(!this.isApplicationMenuOpen);
  }

  closeApplicationMenu() {
    this.setApplicationMenuState(false);
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.removeKeydownListener = this.renderer.listen(
        'document',
        'keydown',
        this.handleKeyDown
      );
    }
  }

  ngOnDestroy() {
    this.closeApplicationMenu();
    if (this.removeKeydownListener) {
      this.removeKeydownListener();
    }
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeApplicationMenu();
      this.closeDropdown();
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };

  private setApplicationMenuState(isOpen: boolean): void {
    this.isApplicationMenuOpen = isOpen;
    if (!isOpen) this.closeDropdown();

    if (isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(document.body, 'overflow', isOpen ? 'hidden' : '');
    }
  }

  toggleNotificationPanel() { this.isNotificationPanelOpen = !this.isNotificationPanelOpen; }

  signOut() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    const clientId = this.authState.getClientId();
    const redirectUrl = this.authState.getLogoutRedirectUrl();

    this.authService.logout(clientId ?? undefined)
      .pipe(finalize(() => { this.isLoggingOut = false; }))
      .subscribe({
        next: () => {
          this.authState.clearAuth();
          this.closeDropdown();
          if (typeof window !== 'undefined') {
            window.location.href = redirectUrl;
          }
        },
        error: () => {
          // Even if API fails, clear local auth to force re-login
          this.authState.clearAuth();
          this.closeDropdown();
          if (typeof window !== 'undefined') {
            window.location.href = redirectUrl;
          }
        }
      });
  }

  closeDropdown() { this.shareMenu?.close(); }
  /** "Share the love" opened — close the user menu so only one is showing. */
  onShareMenuOpened() { this.userDropdown?.closeDropdown(); }

  // ── CREATE POST / EVENT (shared dialogs) ─────────────────────────────────

  openPostModal() {
    this.closeApplicationMenu();
    this.createContent?.openPostModal();
  }

  openEventModal() {
    this.closeApplicationMenu();
    this.createContent?.openEventModal();
  }
}
